<template>
  <div id="order-queries" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading retailer order queries..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions instructionKey="order-queries"></i-instructions>
          </v-col>
        </v-row>

        <i-local-filter
          class="mt-3 mb-2"
          :filterPlaceholder="`Search Orders Queries by Reference Number or Order Number or PO Number or ${isILR ? 'Customer' : 'Retailer'}`"
          @filter="localFilter"
        />

        <template v-if="!loading && filteredOrderQueries.length === 0">
          <i-empty-results
            buttonText="Dashboard"
            buttonRouteName="dashboard"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <v-row no-gutters>
            <v-col cols="12">
              <template v-if="$vuetifyDisplay.smAndDown">
                <div
                  class="mt-4 pa-0"
                  v-for="item in filteredOrderQueries"
                  :key="item.id"
                >
                  <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span
                          ><strong>{{ headers[0].text }}</strong></span
                        >
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span
                          ><strong>{{ item.referenceNumber }}</strong></span
                        >
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[1].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ item.retailerOrderReferences[0] }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[2].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ item.supplierOrderNumber }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[3].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(item.orderDateTime)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[4].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(item.createdAt)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[5].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <i-payment-type
                          v-if="item.paymentType"
                          :paymentType="item.paymentType"
                          :textOnly="true"
                        />
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[6].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ item.storeName }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[7].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.currency(
                            displayCreditAmount(item.requestedCreditAmount)
                          )
                        }}</span>
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <p
                      v-if="item.status === 'OUTSTANDING'"
                      class="g-bold g-border-radius-s i-status-block-orange px-2 py-3 text-center"
                    >
                      <strong>QUERY STATUS: OUTSTANDING</strong>
                    </p>

                    <v-divider class="my-2"></v-divider>

                    <v-btn
                      rounded
                      class="i-btn-block-secondary g-full-width"
                      :to="{
                        name: 'order-query-details',
                        params: { id: item.id }
                      }"
                    >
                      <strong> View </strong>
                    </v-btn>
                  </v-card>
                </div>
              </template>

              <template v-else>
                <v-data-table
                  class="i-data-table mt-2 mt-md-4 pt-2 elevation-1"
                  :headers="headers"
                  :loading="loading"
                  :items="filteredOrderQueries"
                  :items-per-page="-1"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                >
                  <template v-slot:[`item.referenceNumber`]="{ item }">
                    <span>{{ item.referenceNumber }}</span>
                  </template>

                  <template v-slot:[`item.storeName`]="{ item }">
                    <span>{{ item.storeName }}</span>
                  </template>

                  <template v-slot:[`item.supplierOrderNumber`]="{ item }">
                    <span>{{ item.supplierOrderNumber }}</span>
                  </template>

                  <template v-slot:[`item.orderDateTime`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.orderDateTime)
                    }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template v-slot:[`item.paymentType`]="{ item }">
                    <i-payment-type
                      v-if="item.paymentType"
                      :paymentType="item.paymentType"
                      :textOnly="true"
                    />
                  </template>

                  <template v-slot:[`item.requestedCreditAmount`]="{ item }">
                    <span>{{
                      $filters.currency(
                        displayCreditAmount(item.requestedCreditAmount)
                      )
                    }}</span>
                  </template>

                  <template v-slot:[`item.orderQuery`]="{ item }">
                    <v-btn
                      rounded
                      class="i-btn-block-secondary p-btn"
                      :to="{
                        name: 'order-query-details',
                        params: { id: item.id }
                      }"
                    >
                      <span><strong>View</strong></span>
                    </v-btn>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <p
                      v-if="item.status === 'OUTSTANDING'"
                      class="g-bold g-border-radius-s i-status-block-orange px-2 py-3 my-1 text-center"
                    >
                      OUTSTANDING
                    </p>
                  </template>
                </v-data-table>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { fetchOrderQueries } from '@/services/orderQueries'
import iOverlay from '@/components/Overlay'
import iLocalFilter from '@/components/LocalFilter'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import iPaymentType from '@/components/PaymentType'
import {
  buildSupplierTotalLabel,
  pickSupplierCostTotal
} from '@/utils/supplierAmountDisplay'

const ORDER_QUERIES_STATUS = ['OUTSTANDING']

export default {
  components: {
    iPaymentType,
    iEmptyResults,
    iInstructions,
    iOverlay,
    iLocalFilter
  },

  data: () => ({
    loading: false,
    currentPage: 1,
    emptyFilteredResultsText: 'No Outstanding Order Queries found',
    pageSize: 1000,
    pagination: {
      total: 0,
      totalPages: 0
    },
    orderQueries: [],
    filterText: ''
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('orderQueries', ['outstandingOrderQueries']),

    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),

    headers() {
      return [
        {
          text: 'ORDER QUERY REFERENCE NUMBER',
          value: 'referenceNumber',
          width: '14%'
        },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'} ORDER NUMBER`,
          value: 'retailerOrderReferences[0]',
          width: '12%'
        },
        { text: 'PO NUMBER', value: 'supplierOrderNumber', width: '7%' },
        { text: 'ORDER DATE & TIME', value: 'orderDateTime', width: '10%' },
        { text: 'CREATED DATE', value: 'createdAt', width: '10%' },
        {
          text: 'PAYMENT TYPE',
          value: 'paymentType',
          align: 'center',
          sortable: false,
          width: '7%'
        },
        { text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`, value: 'storeName' },
        {
          text: buildSupplierTotalLabel(
            this.tenantCountryRegion,
            'TOTAL CREDIT AMOUNT'
          ),
          value: 'requestedCreditAmount',
          align: 'center',
          width: '8%'
        },
        {
          text: 'DETAILS',
          value: 'orderQuery',
          align: 'center',
          sortable: false
        },
        { text: 'STATUS', value: 'status', align: 'center' }
      ]
    },

    filteredOrderQueries() {
      return this.orderQueries.filter((order) => {
        return (
          order.referenceNumber.includes(this.filterText) ||
          order.retailerOrderReferences[0].includes(this.filterText) ||
          order.supplierOrderNumber.includes(this.filterText) ||
          order.storeName.includes(this.filterText)
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('orderQueries', ['updateOutstandingCount']),

    displayCreditAmount(amount) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: amount
      })
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    fetch(currentPage, pageSize, status) {
      this.loading = true

      // currentPage decreased by 1 as currentPage API starts from 0
      currentPage -= 1

      return fetchOrderQueries(
        this.selectedSupplierId,
        currentPage,
        pageSize,
        status
      )
        .then((res) => {
          this.orderQueries = res.queries
          this.pagination = res.pagination
          this.updateOutstandingCount(this.orderQueries.length)
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    this.fetch(this.currentPage, this.pageSize, ORDER_QUERIES_STATUS)
  },

  watch: {
    outstandingOrderQueries() {
      this.fetch(this.currentPage, this.pageSize, ORDER_QUERIES_STATUS)
    }
  }
}
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 960px) {
  #order-queries {
    th span {
      font-size: 13px;
    }
    .v-data-table-header__icon {
      opacity: 1;
      margin-bottom: 3px;
    }
    .v-data-table-header__icon:hover {
      color: black;
    }
  }
}
</style>
